import React, { useState } from 'react';
import { Upload, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import type { FamilyTreeRecord } from '../../lib/adapters';

interface GalleryPhoto {
  id: string;
  r2Key: string;
  caption: string;
  location: string;
  year: string;
}

interface GalleryManagerProps {
  trees: FamilyTreeRecord[];
}

/**
 * Admin gallery manager — upload/delete photos from the admin dashboard.
 * Lives behind the authenticated admin dashboard, so only admins can mutate.
 */
export const GalleryManager: React.FC<GalleryManagerProps> = ({ trees }) => {
  const { token } = useAuthStore();
  const [selectedTreeId, setSelectedTreeId] = useState<string>(trees[0]?.id ?? '');
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ caption: '', location: '', year: '' });
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const apiBase = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '');

  const loadPhotos = async (treeId: string) => {
    if (!treeId || !token) return;
    setError(null);
    try {
      const res = await fetch(`${apiBase}/gallery/${treeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load gallery');
      const data = await res.json();
      setPhotos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gallery');
      setPhotos([]);
    }
  };

  const handleTreeSelect = (treeId: string) => {
    setSelectedTreeId(treeId);
    setPhotos([]);
    loadPhotos(treeId);
  };

  React.useEffect(() => {
    if (selectedTreeId) loadPhotos(selectedTreeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreeId]);

  const handleUpload = async (file: File) => {
    if (!selectedTreeId || !token) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('caption', uploadForm.caption);
      formData.append('location', uploadForm.location);
      formData.append('year', uploadForm.year);

      const res = await fetch(`${apiBase}/gallery/${selectedTreeId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }
      const newPhoto = await res.json();
      setPhotos(prev => [newPhoto, ...prev]);
      setShowUpload(false);
      setUploadForm({ caption: '', location: '', year: '' });
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId: string) => {
    if (!token) return;
    if (!confirm('Delete this photo?')) return;
    setError(null);
    try {
      const res = await fetch(`${apiBase}/gallery/${photoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete photo');
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      if (selected === photoId) setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo');
    }
  };

  const photoUrl = (r2Key: string) => `${apiBase}/photos/${r2Key}`;

  const currentIndex = photos.findIndex(p => p.id === selected);
  const current = currentIndex >= 0 ? photos[currentIndex] : null;
  const prev = () => setSelected(photos[(currentIndex - 1 + photos.length) % photos.length].id);
  const next = () => setSelected(photos[(currentIndex + 1) % photos.length].id);

  return (
    <div className="space-y-6">
      {/* Tree selector + upload button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Tree</label>
          <select
            value={selectedTreeId}
            onChange={e => handleTreeSelect(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
          >
            {trees.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => setShowUpload(s => !s)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-800 transition-colors"
        >
          <Upload className="w-4 h-4" />
          Upload Photo
        </button>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</p>}

      {/* Upload form */}
      {showUpload && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            Upload photo to {trees.find(t => t.id === selectedTreeId)?.name}
          </h3>
          <div className="space-y-4">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
              }}
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-green-500 hover:text-green-700 transition-colors"
            >
              <Upload className="w-8 h-8 mx-auto mb-2" />
              {uploading ? 'Uploading...' : 'Click to select a photo'}
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Caption"
                value={uploadForm.caption}
                onChange={e => setUploadForm({ ...uploadForm, caption: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
              <input
                type="text"
                placeholder="Location"
                value={uploadForm.location}
                onChange={e => setUploadForm({ ...uploadForm, location: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
              <input
                type="text"
                placeholder="Year"
                value={uploadForm.year}
                onChange={e => setUploadForm({ ...uploadForm, year: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowUpload(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <h2 className="font-semibold text-gray-900 mb-3">
          Photos <span className="text-gray-400 font-normal">({photos.length})</span>
        </h2>
        {photos.length === 0 ? (
          <p className="py-12 text-center text-gray-400">
            {showUpload ? 'Select a photo to upload.' : 'No photos yet. Upload the first one!'}
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map(photo => (
              <div key={photo.id} className="relative group aspect-square overflow-hidden rounded-xl shadow-sm">
                <img
                  src={photoUrl(photo.r2Key)}
                  alt={photo.caption}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                  onClick={() => setSelected(photo.id)}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end justify-end p-2">
                  <button
                    onClick={() => handleDelete(photo.id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 bg-white/90 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-colors"
                    title="Delete photo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 p-2 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="font-medium">{photo.caption || 'No caption'}</p>
                  {photo.year && <p className="text-white/70">{photo.year}{photo.location && ` • ${photo.location}`}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selected && current && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <button
            onClick={() => setSelected(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={prev}
            className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={next}
            className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <div className="max-w-3xl w-full">
            <img src={photoUrl(current.r2Key)} alt={current.caption} className="w-full max-h-[70vh] object-contain rounded-lg" />
            <div className="text-white text-center mt-4">
              <p className="text-lg font-medium">{current.caption || 'No caption'}</p>
              <p className="text-white/70 text-sm">
                {current.year} {current.location && `• ${current.location}`}
              </p>
              <button
                onClick={() => handleDelete(current.id)}
                className="mt-2 text-red-400 hover:text-red-300 text-sm underline"
              >
                Delete photo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
